/* Native cart handoff. Manufacturing and work orders are handled by the merchant's ERP. */
(() => {
  'use strict';
// Staged adapter: builds native Cart lines; performs no network request and does not activate the live theme.
function resolveConfiguration(catalog, {vision, packageId, colourId}) {
  if (!['single','none','progressive'].includes(vision)) throw Error('Choose a supported vision type.');
  if (catalog.schema !== 'direct-rx-material-addons-v2' || catalog.currency !== 'USD') throw Error('Wrong lens catalog.');
  const matches = catalog.variants.filter(v => v.vision === vision && v.packageId === packageId);
  if (matches.length !== 1) throw Error('Lens variant is not mapped.');
  const lens = matches[0];
  if (!Number.isSafeInteger(catalog.featureFee) || catalog.featureFee < 0 || !Number.isSafeInteger(lens.variantId) || lens.variantId <= 0 || lens.fee !== lens.baseFee + lens.features.length*catalog.featureFee) throw Error('Invalid lens price mapping.');
  const colour = lens.colours.find(c => c.id === colourId);
  if (!colour) throw Error('Choose an available lens colour.');
  return {lens,colour};
}

function buildPair(catalog, selection, {eyewearVariantId, group, quantity=1, prescriptionProperties={}}) {
  const {lens,colour} = resolveConfiguration(catalog,selection);
  if (!Number.isSafeInteger(eyewearVariantId) || eyewearVariantId <= 0 ||
      !Number.isSafeInteger(quantity) || quantity < 1 || quantity > 99 || !/^RX-[\w-]{8,80}$/.test(group)) throw Error('Invalid pair identity or quantity.');
  // Existing prescription fields/files remain owned by the native three-step form.
  const common = {'Pair reference':group, 'Eyewear variant':String(eyewearVariantId), '_rx_components':'2', 'Prescription route':'Direct Rx'};
  const features = lens.features.map(f => ({photochromic:'Photochromic',polarized:'Polarized',mirror:'Mirror'})[f]);
  const properties = {
    ...prescriptionProperties, ...common, 'Pair component':'Direct prescription lenses',
    'Vision':lens.visionLabel, 'Lens package':lens.packageId, 'Lens material':lens.material,
    'Prescription lens colour':colour.label, 'Lens features':features.join(' + ') || 'Clear',
    'Material base (USD)':(lens.baseFee/100).toFixed(2),
    'Feature add-ons (USD)':((lens.fee-lens.baseFee)/100).toFixed(2),
    'Lens total (USD)':(lens.fee/100).toFixed(2),
    '_rx_schema':catalog.schema, '_rx_lens_colour_id':colour.id, '_rx_feature_mask':String(lens.featureMask)
  };
  return [
    {id:eyewearVariantId,quantity,properties:{...common,'Pair component':'Eyewear'}},
    {id:lens.variantId,quantity,properties}
  ];
}


  const makeItems = (input,settings,group,buildOrderPreview) => {
    if (!input.enabled || input.mode!=='lenses' || settings.route!=='direct' || settings.demo_only)
      throw new Error('Choose your replacement lenses before adding this pair.');
    const frame=settings.frameVariants.find(v=>String(v.id)===String(input.variant_id));
    if (!frame?.available) throw new Error('This frame is currently unavailable.');
    const config=input.config;
    const {lens}=resolveConfiguration(settings.catalog,{vision:config.type,packageId:config.lens,colourId:config.erColourId});
    const actual=settings.lenses.find(v=>v.id===lens.variantId);
    if (!actual?.available) throw new Error('These lenses are currently unavailable.');
    const pack=settings.packages.find(p=>p.id===config.lens);
    if (!pack || actual.price!==pack.fee) throw new Error('Lens pricing changed. Reopen the form before continuing.');
    const preview=buildOrderPreview({variant:{id:frame.id,price:frame.price},insertPrice:0,route:'direct',lensCatalog:settings.packages,
      enabled:true,mode:'lenses',config,groupId:group,revision:1,currency:settings.currency||'USD'});
    const rx=preview.work_order.prescription;
    const properties={'Prescription method':rx.method};
    if (rx.required) {
      properties['Prescription review']={later:'Send Later',upload:'Upload Prescription',manual:'Enter Manually'}[rx.method];
      for (const [side,label] of [['right','Right (OD)'],['left','Left (OS)']])
        for (const key of ['sph','cyl','axis','add']) if(rx[side][key]!=null) properties[`${label} ${key.toUpperCase()}`]=String(rx[side][key]);
      if(rx.pd.mode==='single' && rx.pd.total!=null) properties['PD (mm)']=String(rx.pd.total);
      else if(rx.pd.mode==='dual') {
        if(rx.pd.right!=null)properties['Right PD (mm)']=String(rx.pd.right);
        if(rx.pd.left!=null)properties['Left PD (mm)']=String(rx.pd.left);
      }
      if(rx.fitting_height_mm!=null)properties['Fitting height (mm)']=String(rx.fitting_height_mm);
    }
    const items=buildPair(settings.catalog,{vision:config.type,packageId:config.lens,colourId:config.erColourId},
      {eyewearVariantId:frame.id,group,quantity:input.quantity??1,prescriptionProperties:properties});
    if (input.notes) items[0].properties['Order Notes']=String(input.notes);
    return items;
  };
  class NativeCart {
    constructor({settings,progress=()=>{},buildOrderPreview,request=(...args)=>fetch(...args),send,telemetry=()=>{}}) {
      Object.assign(this,{settings,progress,buildOrderPreview,request,telemetry});this.send=send||this.upload.bind(this);this.pending=null;this.saved=null;
    }
    async json(path,options={}) {
      const response=await this.request(`${typeof window!=='undefined'?window.Shopify?.routes?.root||'/':'/'}${path}`,{...options,cache:'no-store'});
      const data=await response.json();if(!response.ok)throw new Error(data.description||data.message||'Unable to update your cart. Please retry.');return data;
    }
    upload(body) {
      return new Promise((resolve,reject)=>{
        const xhr=new XMLHttpRequest();xhr.open('POST',`${window.Shopify?.routes?.root||'/'}cart/add.js`);xhr.timeout=120000;
        xhr.upload.onprogress=e=>{if(e.lengthComputable)this.progress(`Adding your pair… ${Math.round(e.loaded/e.total*100)}%`);};
        xhr.onload=()=>{let data;try{data=JSON.parse(xhr.responseText);}catch{reject(new Error('Unable to confirm your cart. Please retry.'));return;}xhr.status>=200&&xhr.status<300?resolve(data):reject(new Error(data.description||'Unable to add this pair. Please retry.'));};
        xhr.onerror=xhr.ontimeout=()=>reject(new Error('Connection interrupted. Please retry to confirm your cart.'));
        xhr.send(body);
      });
    }
    submit(input,file) {
      if(this.pending)return this.pending;
      const actionId = /^[a-f0-9-]{36}$/.test(input.action_id || '') ? input.action_id : crypto.randomUUID(), started = Date.now();
      const observed = input.enabled && input.mode === 'lenses';
      const emit = (name, details) => {
        try {
          if (observed) this.telemetry(name, {action_id:actionId, ...details});
          else if (name === 'rx_cart_attempted' || name === 'rx_cart_result') {
            // Standard eyewear uses this cart client too; keep its result out of the Rx funnel.
            const {rx_pair_id, rx_retry_reconciled, rx_error_code, ...fields} = details;
            this.telemetry(name === 'rx_cart_attempted' ? 'add_to_cart_attempted' : 'add_to_cart_result', {
              action_id:actionId, ...fields, result_final:name === 'rx_cart_result',
              ...(rx_error_code ? {error_category:rx_error_code} : {})
            });
          }
        } catch (_) { /* Measurement must never interrupt a cart update. */ }
      };
      emit('rx_cart_attempted', {});
      this.pending=this.perform(input,file,(name, details)=>emit(name, details)).then(result => {
        emit('rx_cart_result', {rx_pair_id:this.saved?.group, outcome:'confirmed_success', rx_retry_reconciled:result.already_added, latency_ms:Date.now()-started});
        return result;
      }).catch(error => {
        const message = String(error?.message || '');
        const code = /incomplete|changed in/.test(message) ? 'pair_incomplete' : /upload was not confirmed/.test(message) ? 'upload_unconfirmed' : /Choose your prescription file again/.test(message) ? 'file_required' : /unavailable/.test(message) ? 'unavailable' : /pricing/.test(message) ? 'pricing' : 'cart_unconfirmed';
        emit('rx_cart_result', {rx_pair_id:this.saved?.group, outcome:['pair_incomplete','file_required','unavailable','pricing'].includes(code)?'confirmed_failure':'unknown', rx_error_code:code, latency_ms:Date.now()-started});
        throw error;
      }).finally(()=>{this.pending=null;});return this.pending;
    }
    async perform(input,file,observe=()=>{}) {
      const signature=JSON.stringify(input);
      if(!this.saved||this.saved.signature!==signature||this.saved.file!==file) {
        const group=`RX-${crypto.randomUUID()}`;
        const items=makeItems(input,this.settings,group,this.buildOrderPreview);
        if(input.enabled&&input.mode==='lenses'&&input.config.type!=='none'&&input.config.method==='upload'&&!file) throw new Error('Choose your prescription file again before adding your pair.');
        this.saved={signature,file,group,items};
      }
      const {group,items}=this.saved;
      const inspect=cart=>{
        const lines=cart.items.filter(l=>l.properties?.['Pair reference']===group);
        if(!lines.length)return false;
        if(lines.length!==items.length||!items.every(i=>lines.some(l=>String(l.variant_id)===String(i.id)&&l.quantity===i.quantity&&Object.entries(i.properties).every(([k,v])=>l.properties[k]===v)))) throw new Error('This pair is incomplete or was changed in your cart. Review your cart before continuing.');
        if(file && !lines.some(l=>/^https?:\/\//.test(l.properties?.['Prescription file']||'') || (l.properties?.['Prescription file']||'').includes('/uploads/'))) throw new Error('Your prescription upload was not confirmed. Please review your cart before checkout.');
        return true;
      };
      // Plain eyewear uses a private retry key as well, without creating an Rx pairing.
      if(!input.enabled)items[0].properties['_rx_cart_request']=group;
      const confirmed=cart=>input.enabled?inspect(cart):cart.items.some(l=>l.properties?._rx_cart_request===group&&String(l.variant_id)===String(input.variant_id)&&l.quantity===items[0].quantity);
      if(confirmed(await this.json('cart.js')))return {already_added:true};
      const body=new FormData();
      items.forEach((item,index)=>{body.append(`items[${index}][id]`,String(item.id));body.append(`items[${index}][quantity]`,String(item.quantity));for(const [key,value]of Object.entries(item.properties))body.append(`items[${index}][properties][${key}]`,value);});
      if(file)body.append(`items[${items.length-1}][properties][Prescription file]`,file,file.name);
      this.progress(file?'Uploading your prescription and adding your pair…':'Adding your pair…');
      const uploadStarted = Date.now();
      if(file)observe('rx_upload_attempted', {rx_pair_id:group});
      try {
        try{await this.send(body);}catch(error){if(!confirmed(await this.json('cart.js')))throw error;}
        if(!confirmed(await this.json('cart.js')))throw new Error('Your pair could not be confirmed. Please retry.');
        if(file)observe('rx_upload_result', {rx_pair_id:group, outcome:'confirmed_success', latency_ms:Date.now()-uploadStarted});
      } catch(error) {
        if(file)observe('rx_upload_result', {rx_pair_id:group, outcome:'unknown', rx_error_code:'upload_unconfirmed', latency_ms:Date.now()-uploadStarted});
        throw error;
      }
      return {already_added:false};
    }
  }
  if(typeof window!=='undefined')window.EmberRxNativeCart=NativeCart;
  if(typeof module!=='undefined'&&module.exports)module.exports={NativeCart,makeItems};
})();
